export function getCurrentScrollTop ({ document, window }) {
  if (document.documentElement?.classList.contains('over-hidden')) {
    return document.body?.scrollTop || 0
  }
  return window.scrollY || window.pageYOffset || 0
}

export function createPopupScrollLock ({
  document,
  window,
  requestFrame = cb => window.requestAnimationFrame ? window.requestAnimationFrame(cb) : setTimeout(cb, 0),
  setDelay = cb => setTimeout(cb, 80),
  clearDelay = id => clearTimeout(id),
  createObserver = cb => typeof MutationObserver !== 'undefined' ? new MutationObserver(cb) : null,
  maxUnlockRetries = 6
}) {
  const popupSelector = '.el-dialog__wrapper, .el-message-box__wrapper'
  const state = {
    isPopupOpen: false,
    scrollTop: 0,
    transitionId: 0,
    unlockDelayId: null,
    unlockPending: false,
    observer: null,
    popupObservers: new Map()
  }

  function getHtml () {
    return document.documentElement || document.getElementsByTagName('html')[0]
  }

  function isBodyLocked (classValue) {
    return String(classValue || '').split(' ').includes('el-popup-parent--hidden')
  }

  function hasVisiblePopup () {
    return Array.from(document.querySelectorAll?.(popupSelector) || [])
      .some((element) => {
        if (window.getComputedStyle) {
          const style = window.getComputedStyle(element)
          return style.display !== 'none' && style.visibility !== 'hidden'
        }
        return element.offsetParent !== null
      })
  }

  function clearUnlockDelay () {
    if (state.unlockDelayId !== null) {
      clearDelay(state.unlockDelayId)
      state.unlockDelayId = null
    }
    state.unlockPending = false
  }

  function getBodyClassValue () {
    return document.body?.getAttribute?.('class') || document.body?.className || ''
  }

  function isPopupElement (element) {
    return Boolean(
      element?.matches?.(popupSelector) ||
      element?.closest?.(popupSelector) ||
      element?.querySelector?.(popupSelector)
    )
  }

  function shouldSyncMutation (mutation) {
    if (mutation.target === document.body && mutation.attributeName === 'class') {
      return true
    }
    if (mutation.type === 'attributes') {
      return isPopupElement(mutation.target)
    }
    return Array.from(mutation.addedNodes || []).some(isPopupElement) ||
      Array.from(mutation.removedNodes || []).some(isPopupElement)
  }

  function observePopupElement (element, sync) {
    if (!element?.matches?.(popupSelector) || state.popupObservers.has(element)) {
      return
    }

    const observer = createObserver((mutations = []) => {
      if (!mutations.length || mutations.some(shouldSyncMutation)) {
        sync(getBodyClassValue())
      }
    })

    if (!observer) {
      return
    }

    observer.observe(element, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      subtree: false
    })
    state.popupObservers.set(element, observer)
  }

  function syncPopupObservers (sync) {
    const popupElements = Array.from(document.querySelectorAll?.(popupSelector) || [])
    popupElements.forEach(element => observePopupElement(element, sync))

    Array.from(state.popupObservers.keys()).forEach((element) => {
      if (!popupElements.includes(element)) {
        state.popupObservers.get(element)?.disconnect()
        state.popupObservers.delete(element)
      }
    })
  }

  function disconnectPopupObservers () {
    state.popupObservers.forEach(observer => observer.disconnect())
    state.popupObservers.clear()
  }

  function release () {
    state.isPopupOpen = false
    state.unlockPending = false
    getHtml()?.classList.remove('over-hidden')
    window.scrollTo(0, state.scrollTop)
  }

  function lock () {
    clearUnlockDelay()
    if (!state.isPopupOpen) {
      state.scrollTop = getCurrentScrollTop({ document, window })
    }
    state.transitionId += 1
    state.isPopupOpen = true
    getHtml()?.classList.add('over-hidden')
    document.body?.scrollTo?.(0, state.scrollTop)
  }

  function unlock () {
    if (state.unlockPending) {
      return
    }
    state.unlockPending = true
    const unlockId = state.transitionId
    const tryUnlock = (retryCount = 0) => {
      requestFrame(() => {
        requestFrame(() => {
          state.unlockDelayId = setDelay(() => {
            state.unlockDelayId = null
            if (unlockId !== state.transitionId || document.body?.classList.contains('el-popup-parent--hidden')) {
              state.unlockPending = false
              return
            }
            if (hasVisiblePopup()) {
              if (retryCount < maxUnlockRetries) {
                tryUnlock(retryCount + 1)
              } else {
                state.unlockPending = false
              }
              return
            }
            release()
          })
        })
      })
    }
    tryUnlock()
  }

  return {
    start () {
      if (state.observer || !document.body) {
        return
      }

      const observer = createObserver((mutations = []) => {
        if (!mutations.length || mutations.some(shouldSyncMutation)) {
          this.sync(getBodyClassValue())
        }
        syncPopupObservers(this.sync.bind(this))
      })

      if (!observer) {
        return
      }

      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: false
      })
      state.observer = observer
      syncPopupObservers(this.sync.bind(this))
      this.sync(getBodyClassValue())
    },
    sync (classValue) {
      if (isBodyLocked(classValue)) {
        lock()
      } else if (state.isPopupOpen) {
        unlock()
      }
    },
    reset () {
      clearUnlockDelay()
      state.transitionId += 1
      state.isPopupOpen = false
      getHtml()?.classList.remove('over-hidden')
    },
    destroy () {
      clearUnlockDelay()
      if (state.observer) {
        state.observer.disconnect()
        state.observer = null
      }
      disconnectPopupObservers()
      state.transitionId += 1
      state.isPopupOpen = false
      getHtml()?.classList.remove('over-hidden')
    },
    getState () {
      return { ...state }
    }
  }
}

export default createPopupScrollLock
